// JO, ML: for some reason running cli scripts results in the type of this being
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
// unresolved, despite the fact that typings exist (and builds & dev server work fine)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { chunk, Dictionary, flatten, get, identity, isNil, mapValues, pickBy, some, sum, uniq, values } from 'lodash';
import pLimit from 'p-limit';
import { compare, valid } from 'semver';
import { EnumLike, z } from 'zod';
import { isEevoHost } from './arvo/public-url';
import { getEnvironment, isAwsEnvironment, isDev, isTest } from './environment';
import { UprightSite } from './environment/types';
import { getFeatureAccessMessageType } from './iam/access/feature-access';
import { userGroupsConsideredMicroWorkers } from './iam/access/feature-access/feature-accesses';
import { FeatureAccessMessageType, FeatureId } from './iam/access/feature-access/types';
import { Access, AccessSubjectType, EntityAccess } from './iam/access/types';
import { assertLicenceResolved, toUserGroupClosure, UserGroupClosure } from './iam/entitlement/licence';
import { getOrganizationOfUser } from './iam/organizations/common';
import { Organization, OrganizationType } from './iam/organizations/organization-types';
import { SpecialUserGroupId, UserGroup } from './iam/user-groups/types';
import { User, UserWithIdAndPlainGroups, UserWithPlainGroups } from './iam/users/types';
import { pickDefaultModelResult } from './model/data-point-pickers';
import { MIN_EMPLOYEE_COUNT } from './model/input/companies/data-points/employee-counts/consts';
import { MIN_REVENUE_IN_USD } from './model/input/companies/data-points/revenues/consts';
import { impactNamesByLanguage } from './model/input/impacts';
import { ProductTitleByCode } from './model/input/products/types';
import { ImpactLanguage, ModelEntityType } from './model/input/types';
import { predictedLabel } from './model/output/article-predictions';
import { PredictionByClassifier, ProbabilityByLabel } from './model/output/article-predictions/types';
import { ModelEntityWithScores } from './model/output/scores/model-entity-data/types';
import { FinalImpactScoreType, ImpactScoresByPIVV } from './model/output/scores/types';
import { ExtendedValence, Valence } from './model/types';
import { convertToUsd as convertToUSD } from './utils/currency';
import { NumberHash } from './utils/number-hash/types';

export function getSiteUrl(site: UprightSite) {
  switch (site) {
    case UprightSite.PLATFORM:
      return getUiUrl();
    case UprightSite.FACADE:
      return 'https://www.uprightproject.com';
  }
}

export function getUnknownCurrentUserNameForPath() {
  return 'me';
}

export function getUiUrl() {
  // support preview deployments (*.uprightdb.com), and Eevo's own hosts (eevo.io), which serve the
  // same build as the platform
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname.endsWith('.uprightdb.com') || isEevoHost(window.location.hostname))
  ) {
    return window.location.origin;
  }

  if (isAwsEnvironment()) {
    return process.env.UI_URL || '';
  }

  if (isDev() || isTest()) {
    // Returns localhost-production and localhost-staging correctly. They are needed for the SSO redirects to work.
    return (typeof window !== 'undefined' && window.location.origin) || process.env.UI_URL || `http://localhost:3333`;
  }

  throw new Error(`Unknown environment: ${getEnvironment() as string}`);
}

export function getModelEntityUrlInServerCode(type: ModelEntityType, id: string, name?: string) {
  const namePart = name ? encodeURIComponent(name) : '';
  switch (type) {
    case ModelEntityType.COMPANY:
      return `/company/${id}/${namePart}`;
    case ModelEntityType.PORTFOLIO:
      return `/portfolio/${id}/${namePart}`;
    case ModelEntityType.GROUP:
      return `/company-group/${id}/${namePart}`;
    case ModelEntityType.PRODUCT:
    case ModelEntityType.MIXIN:
    case ModelEntityType.PHENOMENON:
      return `/product/${id}/${namePart}`;
  }
}

export async function sleep(ms = 0) {
  return await new Promise(r => setTimeout(r, ms));
}

// TODO: refactor in such a way that this code
// does not have to be shared

export interface DbProbabilityByLabel {
  probP: number;
  probN: number;
  probO: number;
  probS: number;
}

/**
 */
export function fromDbProbabilityByLabel(model: DbProbabilityByLabel): ProbabilityByLabel {
  return {
    P: model.probP,
    N: model.probN,
    O: model.probO,
    S: model.probS,
  };
}

/**
 * In the database we store probabilities
 * in separate columns. This helper functions
 * converts such rows to
 */
export function toDbProbabilityByLabel(datum: ProbabilityByLabel): DbProbabilityByLabel {
  return {
    probP: datum.P,
    probN: datum.N,
    probO: datum.O,
    probS: datum.S,
  };
}

interface PlainClassifierPrediction {
  classifierId: string;
  probN: number;
  probO: number;
  probS: number;
  probP: number;
}

/**
 * Helper for converting from new postgres-based format of predictions
 * to (legacy) format still used in UI
 */
export function plainClassifierPredictionsToPredictionByClassifier(
  predictions: PlainClassifierPrediction[],
): PredictionByClassifier {
  return predictions.reduce<PredictionByClassifier>((result, current) => {
    const probabilityByLabel: ProbabilityByLabel = {
      [ExtendedValence.NEGATIVE]: current.probN,
      [ExtendedValence.POSITIVE]: current.probP,
      [ExtendedValence.OTHER]: current.probO,
      [ExtendedValence.SMALL_NEGATIVE]: current.probS,
    };
    const label = predictedLabel(probabilityByLabel);
    result[current.classifierId] = {
      predictions: probabilityByLabel,
      predictedLabel: label,
    };
    return result;
  }, {});
}

export interface WithValueAndValueCurrency {
  value: number;
  valueCurrency: string;
}

export function hasValueAndValueCurrency(
  datum: Partial<{ value: number | null; valueCurrency: string | null }>,
): datum is WithValueAndValueCurrency {
  return !isNil(datum.value) && !isNil(datum.valueCurrency);
}

export function toUsdWeight(datum: WithValueAndValueCurrency) {
  // If this fails often, can be changed to warning for now
  return convertToUSD(datum.value, datum.valueCurrency);
}

interface UserGroupLike<T> {
  parents?: T[];
  memberGroups?: T[];
}

export function withNestedParentUserGroups<T extends UserGroupLike<T>>(group: T): T[] {
  const parents = group.parents;
  if (!parents) {
    return [group];
  }
  const nested = flatten(parents.map(p => withNestedParentUserGroups(p)));
  return [group, ...nested];
}

export function withNestedChildUserGroups<T extends UserGroupLike<T>>(group: T): T[] {
  const children = group.memberGroups;
  if (!children) {
    return [group];
  }
  const nested = flatten(children.map(c => withNestedChildUserGroups(c)));
  return [group, ...nested];
}

export function getAllParentGroupsOfUser(user: UserWithPlainGroups | undefined | null) {
  if (!user) {
    // Unauthenticated requests get ANONYMOUS in addition to EVERYONE. The open-platform lenses
    // (NI/SDG/EU-t) are granted via ANONYMOUS rather than EVERYONE so that a signed-in customer
    // group can have them removed (EVERYONE is an implicit ancestor of every user and could never
    // be toggled off); logged-out visitors keep seeing those lenses through this branch.
    return [SpecialUserGroupId.EVERYONE, SpecialUserGroupId.ANONYMOUS];
  }

  const { legacyGroupIds, licensedGroupIds } = getUserGroupClosure(user);
  return uniq([
    ...legacyGroupIds,
    ...licensedGroupIds,
    SpecialUserGroupId.EVERYONE,
    SpecialUserGroupId.SIGNED_IN_USERS,
  ]);
}

/**
 * A signed-in user's group closure, from its own groups each in its organization's mode: groups of
 * an organization on the record by its licence, every other group structurally.
 */
export function getUserGroupClosure(user: UserWithPlainGroups): UserGroupClosure {
  assertLicenceResolved(user);
  // OH TODO: Fix the typing to recognize that groups can be undefined
  return toUserGroupClosure((user.groups as UserGroup[] | undefined) ?? []);
}

export function userIsMemberOf(groupId: number | readonly number[], user: UserWithPlainGroups | undefined | null) {
  const allParentGroupsOfUser = getAllParentGroupsOfUser(user);
  if (typeof groupId === 'number') {
    return allParentGroupsOfUser.includes(groupId);
  } else {
    return groupId.some(id => allParentGroupsOfUser.includes(id));
  }
}

/**
 * TEAM membership: the role "every customer's data", plus every internal tool not yet moved to
 * isStaffUser. New customer-data code asks hasAllCustomerDataAccess (UI) or staffAccessOfUser (backend).
 */
export function isTeamUser(user: UserWithPlainGroups | undefined | null) {
  return userIsMemberOf(SpecialUserGroupId.TEAM, user);
}

/** Upright staff, who may use internal tools without customer data. Every TEAM member is one. */
export function isStaffUser(user: UserWithPlainGroups | undefined | null) {
  return userIsMemberOf(SpecialUserGroupId.STAFF, user);
}

/** Whether the user holds the role "every customer's data". The backend decides it in resolveStaffAccess. */
export function hasAllCustomerDataAccess(user: UserWithPlainGroups | undefined | null) {
  return groupIdsHaveAllCustomerDataAccess(getAllParentGroupsOfUser(user));
}

/** The role "every customer's data" is TEAM membership, given every group a subject is in. */
export function groupIdsHaveAllCustomerDataAccess(groupIds: readonly number[]) {
  return groupIds.includes(SpecialUserGroupId.TEAM);
}

export function isAdminUser(user: UserWithPlainGroups | undefined | null) {
  return userIsMemberOf(SpecialUserGroupId.ADMIN, user);
}

export function isMicroworkUser(user: UserWithPlainGroups | undefined | null) {
  return userIsMemberOf(userGroupsConsideredMicroWorkers, user);
}

export function isManualTestUser(user: UserWithPlainGroups | undefined | null) {
  return userIsMemberOf(SpecialUserGroupId.TEST_USERS, user);
}

/**
 * Upright team users OR manual test users seeded into customer orgs.
 *
 * The single predicate the usage-limits machinery keys off when a caller
 * should be treated as "internal" for quota purposes — either an Upright team
 * member, or a flagged test user (TEST_USERS group) sitting inside a customer
 * org. The test-user case is the gap the `@uprightproject.com` email and
 * INTERNAL-org-type signals miss: a test account on a customer domain inside a
 * customer org otherwise spends that customer's shared budget and clutters
 * their usage panel. Both populations get the same quota treatment — their own
 * per-user counter on `byOrgOrTeamUser` quotas, and a full bypass on the
 * org-level chat / connector token budgets — so they share one predicate.
 */
export function isTeamOrTestUser(user: UserWithPlainGroups | undefined | null) {
  return isTeamUser(user) || isManualTestUser(user);
}

export function isInvestorUser(user: UserWithPlainGroups | undefined | null) {
  return (
    userIsMemberOf(SpecialUserGroupId.INVESTOR_STANDARD_CLIENTS, user) ||
    userIsMemberOf(SpecialUserGroupId.INVESTOR_FREE_CLIENTS, user) ||
    isTeamUser(user)
  );
}

export function isDmaFreemiumUser(user: User | undefined | null) {
  if (isNil(user)) return false;
  const organization = getOrganizationOfUser(user);
  return isDmaFreemiumOrg(organization);
}

export function isDmaFreemiumOrg(organization: Organization | undefined) {
  return organization?.type === OrganizationType.DMA_FREEMIUM;
}

export function isDmaConsultantUser(user: User | undefined | null) {
  if (isNil(user)) return false;
  const organization = getOrganizationOfUser(user);
  return isDmaConsultantOrg(organization);
}

export function isDmaConsultantOrg(
  organization: Organization | undefined,
): organization is Organization & { type: OrganizationType.DMA_CONSULTANT } {
  return organization?.type === OrganizationType.DMA_CONSULTANT;
}

export function isDmaUser(user: User | undefined | null) {
  return isDmaFreemiumUser(user) || isDmaConsultantUser(user);
}

export function isDmaOrg(organization: Organization | undefined) {
  return isDmaFreemiumOrg(organization) || isDmaConsultantOrg(organization);
}

/**
 * Whether an organization buys its DMA features as a plan (Starter, Pro or Enterprise): a DMA freemium or
 * corporate organization by its subscription, a consultant per client company. Any other organization, an
 * investor for one, holds no plan, so a plan feature is not withheld from it by tier: what it may do on a
 * company is decided by its privileges there alone.
 */
export function holdsDmaPlans(organization: Pick<Organization, 'type'> | undefined) {
  return (
    organization?.type === OrganizationType.DMA_FREEMIUM ||
    organization?.type === OrganizationType.DMA_CONSULTANT ||
    organization?.type === OrganizationType.COMPANY
  );
}

/** A self-serve organization of the Arvo decision API. */
export function isArvoOrg(organization: Organization | undefined) {
  return organization?.type === OrganizationType.ARVO;
}

export function isInvestorStandardLicenceUser(user: UserWithPlainGroups | undefined | null) {
  return userIsMemberOf(SpecialUserGroupId.INVESTOR_STANDARD_CLIENTS, user) || isTeamUser(user);
}

export function isSelfSignupCompanyUser(user: UserWithPlainGroups | undefined | null) {
  return userIsMemberOf(SpecialUserGroupId.COMPANY_PLATFORM_SELF_SIGNUP_USERS, user);
}

export const COMPANY_USER_GROUPS = [
  SpecialUserGroupId.COMPANY_PLATFORM_FREE_CLIENTS,
  SpecialUserGroupId.COMPANY_PLATFORM_STANDARD_CLIENTS,
] as const;

export function isCompanyUser(user: UserWithPlainGroups | undefined | null) {
  if (isTeamUser(user)) {
    return true;
  }
  for (const group of COMPANY_USER_GROUPS) {
    if (userIsMemberOf(group, user)) {
      return true;
    }
  }
  return false;
}

export function isCompanyStandardLicenseUser(user: UserWithPlainGroups | undefined | null) {
  return userIsMemberOf(SpecialUserGroupId.COMPANY_PLATFORM_STANDARD_CLIENTS, user) || isTeamUser(user);
}

// To be removed, standard coverage group alone is not a good way to distinguish between client types
export function isStandardOrExtendedCoverageUser(user: UserWithPlainGroups | undefined | null) {
  return (
    userIsMemberOf(SpecialUserGroupId.STANDARD_COVERAGE, user) ||
    userIsMemberOf(SpecialUserGroupId.EXTENDED_COVERAGE, user) ||
    isTeamUser(user)
  );
}

export function isClientOrTeamOrFreeTrialUser(user: UserWithPlainGroups | undefined | null) {
  return isTeamUser(user) || userIsMemberOf(SpecialUserGroupId.CLIENTS_AND_FREE_TRIAL_USERS, user);
}

export function seemsLikeUprightTestEmail(email: string) {
  return email.includes('+') && email.endsWith('@uprightproject.com');
}

/**
 * Checks if user is a client or free trial user with organization access.
 * This consolidates the logic for PLG accounts that may not have CLIENTS_AND_FREE_TRIAL_USERS group membership
 * but have organization access.
 */
export function isOrganizationClientOrFreeTrialUser(user: UserWithPlainGroups | undefined | null) {
  return isClientOrTeamOrFreeTrialUser(user) || (user && getOrganizationOfUser(user));
}

export function hasAccessToFeature(
  user: UserWithIdAndPlainGroups | undefined | null,
  featureId: FeatureId,
  entityId?: string,
) {
  return getFeatureAccessMessageType(featureId, user || undefined, entityId) === FeatureAccessMessageType.AVAILABLE;
}

export function modelEntitiesToImpactScoresByPIVV(
  productData: ModelEntityWithScores[],
  scoreType: FinalImpactScoreType.RELATIVE | FinalImpactScoreType.ABSOLUTE = FinalImpactScoreType.RELATIVE,
): ImpactScoresByPIVV {
  return productData.reduce<ImpactScoresByPIVV>((acc, entity) => {
    const modelResult = pickDefaultModelResult(entity.modelResults);
    const scores = modelResult.impactScoresByTIVV[scoreType];
    if (scores) {
      acc[entity.id] = scores;
    }
    return acc;
  }, {});
}

export function compareVersions(a: string, b: string) {
  const [aClean, bClean] = [stripPostfix(a), stripPostfix(b)];
  if (aClean === bClean) {
    return 0;
  }
  if (aClean === 'master') {
    return 1;
  }
  if (bClean === 'master') {
    return -1;
  }

  if (!valid(aClean) || !valid(bClean)) {
    return -1;
  }

  return compare(aClean, bClean);
}

export function safeVersionGte(a: string, b: string) {
  return compareVersions(a, b) >= 0;
}

export function safeVersionGe(a: string, b: string) {
  return compareVersions(a, b) > 0;
}

export function stripPostfix(version: string) {
  return version.replace(/\.(dev|val|sta|pre|development|staging)$/i, '');
}

export interface ListHash {
  [key: string]: boolean;
}

export function toListHash(list: string[]): ListHash {
  return list.reduce<ListHash>((result, current) => {
    result[current] = true;
    return result;
  }, {});
}

export type NoUndefinedField<T> = {
  [P in keyof T]-?: NoUndefinedField<NonNullable<T[P]>>;
};

/**
 * Remove null and undefined values from an object
 * (in a way that TypeScript understands the result)
 */
export function removeNullAndUndefined<T extends object>(obj: T) {
  return pickBy(obj, identity) as Record<string, unknown> as NoUndefinedField<T>;
}

// from https://stackoverflow.com/a/50697299/
export function primitiveToBoolean(value: string | number | boolean | null | undefined): boolean {
  if (value === 'true') {
    return true;
  }

  return typeof value === 'string'
    ? !!+value // we parse string to integer first
    : !!value;
}

export function mergeNumberHash(hashes: NumberHash[]) {
  return hashes.reduce<NumberHash>((result, currentHash) => {
    Object.keys(currentHash).forEach(phrase => {
      if (!result[phrase]) {
        result[phrase] = 0;
      }
      result[phrase] += currentHash[phrase];
    });
    return result;
  }, {});
}

export function normalizeNumberHash(numberHash: NumberHash) {
  if (Object.keys(numberHash).length === 0) {
    return numberHash;
  }
  const total = sum(values(numberHash));
  if (!total) {
    return numberHash;
  }
  return mapValues(numberHash, value => value / total);
}

export function pickLargestFromNumberHash(data: NumberHash, ratio: number): NumberHash {
  const keys = Object.keys(data);

  const ret = { ...data };
  const total = sum(values(data));
  if (!total) {
    return ret;
  }

  // sort
  keys.sort((a, b) => data[b] - data[a]);

  // remove keys with
  let cumulative = 0;
  for (const key of keys) {
    if (cumulative / total >= ratio) {
      delete ret[key];
    }
    cumulative += data[key];
  }

  return ret;
}

export const getAccessSubjectName = (access: {
  subjectType: AccessSubjectType;
  subjectUser?: Access['subjectUser'];
  subjectUserGroup?: Access['subjectUserGroup'];
  subjectName?: EntityAccess['subjectName'];
}) => {
  if (access.subjectName) {
    return access.subjectName;
  }
  switch (access.subjectType) {
    case AccessSubjectType.USER: {
      return access.subjectUser?.username || '';
    }
    case AccessSubjectType.USER_GROUP: {
      return access.subjectUserGroup?.name || '';
    }
    case AccessSubjectType.SESSION:
    default:
      return '';
  }
};

export function replaceAll(str: string, find: string, replace: string) {
  return str.split(find).join(replace);
}

/**
 * Converts null to undefined's for the first-level values of an object.
 */

export function mapNullValuesToUndefined<T extends object>(object: T) {
  return mapValues(object, nullToUndefined) as {
    [P in keyof T]: T[P] extends NonNullable<T[P]>
      ? NonNullable<T[P]>
      : T extends NonNull<T[P]>
        ? NonNull<T[P]>
        : NonNull<T[P]> | undefined;
  };
}

type NonNull<T> = T extends null ? never : T;

export function nullToUndefined<T>(d?: T): NonNullable<T> | undefined {
  if (!isNonNullable(d)) {
    return undefined;
  }
  return d;
}

export function undefinedToNull<T>(d?: T): NonNullable<T> | null {
  if (!isNonNullable(d)) return null;
  return d;
}

function isNonNullable<T>(datum: T | null | undefined): datum is NonNullable<T> {
  return !isNil(datum);
}

export function isTooSmallRevenue(revenueUsd: number | undefined) {
  if (!revenueUsd) {
    return undefined;
  }
  return revenueUsd <= MIN_REVENUE_IN_USD;
}

export function isTooSmallEmployeeCount(count: number | undefined) {
  if (!count) {
    return undefined;
  }
  return count <= MIN_EMPLOYEE_COUNT;
}

// OA: Importing this does seem to break the build sometimes, both ui and server. You can still copy
// and use it if you don't want to separately manage a list of all enum values.
export function getAllEnumValues<T extends object>(inputEnum: T, enumType: 'string' | 'number') {
  const ret = Object.values(inputEnum)
    .map((v: T[keyof T]) => {
      if (typeof v !== enumType) {
        return undefined;
      }
      return v;
    })
    .filter(v => v !== undefined) as Array<T[keyof T]>;

  return ret;
}

export function isValueOfEnum<T extends EnumLike>(value: any, inputEnum: T): value is T[keyof T] {
  const { success } = z.nativeEnum(inputEnum).safeParse(value);
  return success;
}

// OA: Just like Object.keys or lodash.keys, but keeps the typings
// of typed keys intact and doesn't convert them to generic strings
export function keysOf<T extends object>(object: T) {
  const keys = Object.keys(object) as Array<keyof typeof object>;
  return keys;
}

// eslint-disable-next-line upright/no-products-by-code
export function friendlyProductTitle(productCode: string, productsByCode?: ProductTitleByCode) {
  const product = productsByCode && get(productsByCode, productCode, undefined);
  return product ? product.title : productCode;
}

export function friendlyImpactTitle(impactCode: string, lang: ImpactLanguage = ImpactLanguage.en) {
  return impactNamesByLanguage[lang][impactCode as ImpactCategoryId] || impactCode;
}

export function friendlyShortImpactTitle(impactCode: string, lang: ImpactLanguage = ImpactLanguage.en) {
  if (impactCode === 'S5') {
    if (lang === ImpactLanguage.en) {
      return 'Societal stability';
    } else if (lang === ImpactLanguage.fi) {
      return 'Yhteiskunnan vakaus';
    }
  } else if (impactCode === 'S4') {
    if (lang === ImpactLanguage.sv) {
      return 'Jämställdhet';
    }
  }
  return friendlyImpactTitle(impactCode, lang);
}

export function friendlyValence(valence: Valence) {
  switch (valence) {
    case Valence.POSITIVE:
      return 'Positive';
    case Valence.NEGATIVE:
      return 'Negative';
    case Valence.OTHER:
      return 'Other';
    default:
      throw Error(`Invalid valence: ${valence as string}`);
  }
}

export function friendlyLowerCaseScoreUnit(unit: FinalImpactScoreType | undefined) {
  return friendlyScoreUnit(unit).toLowerCase();
}

export function friendlyScoreUnit(unit: FinalImpactScoreType | undefined) {
  switch (unit) {
    case FinalImpactScoreType.CENTS_PER_DOLLAR:
      return 'Cents per dollar of revenue';
    case FinalImpactScoreType.DOLLARS_PER_YEAR:
      return 'Impact dollars per year';
    case FinalImpactScoreType.RELATIVE:
      return 'Classic scores';
    case FinalImpactScoreType.ABSOLUTE:
      return 'Absolute scores';
    case undefined:
      return '';
  }
}

export const unrecognizedUidError = 'Unrecognized UID';

export function getEntityType(entityId: string) {
  // NOTE: keep in sync with fn_validate_access_object_upsert
  if (entityId.startsWith('g-')) {
    return ModelEntityType.GROUP;
  } else if (entityId.startsWith('p-')) {
    return ModelEntityType.PORTFOLIO;
  } else if (entityId.length === 36) {
    return ModelEntityType.COMPANY;
  } else if (entityId.toUpperCase() === entityId && entityId.length === 8) {
    return ModelEntityType.MIXIN;
  } else if (entityId.toUpperCase() === entityId && entityId.length === 5) {
    return ModelEntityType.PRODUCT;
  }
  throw Error(`${unrecognizedUidError}: ${entityId}`);
}

export function isCompanyId(uprightId: string | undefined | null) {
  if (!uprightId) {
    return false;
  }
  return getEntityType(uprightId) === ModelEntityType.COMPANY;
}

export function isPortfolioId(uprightId: string | undefined | null) {
  if (!uprightId) {
    return false;
  }
  return getEntityType(uprightId) === ModelEntityType.PORTFOLIO;
}

export function isGroupId(uprightId: string | undefined | null) {
  if (!uprightId) {
    return false;
  }
  return getEntityType(uprightId) === ModelEntityType.GROUP;
}

export function isGroupOrPortfolioId(uprightId: string | undefined) {
  return isGroupId(uprightId) || isPortfolioId(uprightId);
}

/**
 * Awaits all the provided promises, awaiting only a limited number simultaneously. Similar to chunkedAwaitMap,
 * but instead of waiting for each chunk to complete before starting the next, starts awaiting new promises on
 * a rolling basis when previous ones get completed.
 *
 * @param promises an array of promises
 * @param limit the number of promises being awaited simultaneously
 * @returns A promise of the awaited promises
 */
export async function awaitWithLimit<T extends PromiseLike<unknown>>(promises: T[], limit: number = 5) {
  const limiter = pLimit(limit);
  // eslint-disable-next-line upright/no-dynamic-promise-all
  return await Promise.all(
    promises.map(async promise => {
      return await limiter(async () => await promise);
    }),
  );
}

export async function chunkedAwaitMap<T, U>(
  collection: readonly T[],
  awaitable: (element: T, i?: number) => Promise<U>,
  chunkSize = 10,
): Promise<U[]> {
  const awaitChunks = chunk(collection, chunkSize);
  const allResults: U[][] = [];
  for (const awaitChunk of awaitChunks) {
    // eslint-disable-next-line upright/no-dynamic-promise-all
    const chunkResults = await Promise.all(awaitChunk.map(awaitable));
    allResults.push(chunkResults);
  }
  return flatten(allResults);
}

export async function serialAwaitMap<T, U>(collection: T[], awaitable: (element: T) => Promise<U>) {
  return await chunkedAwaitMap(collection, awaitable, 1);
}

export async function serialAwaitMapValues<T, U>(collection: Dictionary<T>, awaitable: (element: T) => Promise<U>) {
  return await chunkedAwaitMapValues(collection, awaitable, 1);
}

export async function chunkedAwaitMapValues<T, U>(
  collection: Dictionary<T>,
  awaitable: (element: T) => Promise<U>,
  chunkSize = 10,
) {
  const entryPredicate = async ([key, element]: [string, T]) => {
    const result = await awaitable(element);
    return [key, result];
  };
  const entries = await chunkedAwaitMap(Object.entries(collection), entryPredicate, chunkSize);
  return Object.fromEntries(entries) as Dictionary<U>;
}

export function hasDefinedProperty(object: object) {
  return some(values(object), (value: string | object | null | undefined) => value !== undefined);
}

export function requireActualnessOrConfidence(obj: any) {
  // during development, we used the values for "confidence" for "actualness"
  // this conditional exists to support both field names while transitioning
  // the sheets
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  const value = obj['Confidence*'] || obj['Actualness*'];
  if (!value) {
    throw Error(`Actualness (old "confidence") missing in row ${JSON.stringify(obj)}`);
  }
  if (typeof value === 'string') {
    return value;
  }
  throw Error(`Type of actualness/confidence was ${typeof value} in row ${JSON.stringify(obj)}`);
}

export function requireNonEmptyString(obj: any, field: string): string {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  const value = obj[field];
  if (value === '' || value === undefined || value === null) {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    throw Error(`Value was ${value} for field ${field} in row ${JSON.stringify(obj)}`);
  }
  if (typeof value === 'string') {
    return value;
  }
  throw Error(`Type of value was ${typeof value} for field ${field} in row ${JSON.stringify(obj)}`);
}

export function assertIsEqual<T>(actual: unknown, expected: T, message: string): asserts actual is T {
  if (actual !== expected) throw Error(`${message}: expected ${actual as any} === ${expected as any}`);
}

export function filterObjectKeys<T extends Record<string, any>, K extends keyof T>(
  obj: T,
  allowedKeys: K[],
): Pick<T, K> {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const filteredObject = {} as Pick<T, K>;

  allowedKeys.forEach(key => {
    if (key in obj) {
      filteredObject[key] = obj[key];
    }
  });

  return filteredObject;
}

export function sortByCreationDate(a: { createdAt: string }, b: { createdAt: string }) {
  return (a.createdAt || '').localeCompare(b.createdAt || '');
}

export function OppositeNumber<T>(value: T): number | T {
  if (!value) {
    return value;
  }
  const num = Number(value);
  return num * -1;
}

export function isDateExpired(date: string) {
  return new Date(date).getTime() < new Date().getTime();
}
